import React, { useState, useEffect } from "react";
import Container from "react-bootstrap/Container";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import "./Programs.css";
import { useParams } from "react-router-dom";
import Loader from "@shared/components/Loader";
import { useAxiosGet } from "@features/programs/services/apiService";
import {
  isActive,
  getEndDateOfProgram,
  programInterval,
  getTargetName,
} from "@features/programs/utils/programHelpers";
import ExerciseCard from "./ExerciseCard";
import { SharedProgramSidebar } from "@sophy/shared-ui";
import Button from "react-bootstrap/Button";
import Modal from "react-bootstrap/Modal";
import Form from "react-bootstrap/Form";
import http from "@shared/api/http";
import parse from "html-react-parser";
import { ProgramModal } from "@features/programs/modals/ProgramModal";
import { SlideshowModal } from "@features/programs/modals/SlideshowModal";
import { FeedbackForm } from "@features/programs/modals/FeedbackForm";
import QuestionnaireDecoder from "@shared/components/QuestionnaireDecoder";
import { useTranslation } from "react-i18next";
import { createProgramFeedbackNotification, createProgramCompletedNotification, createProgramRequestNotification, canRequestNewProgram, createSubscriptionContactNotification, notifyReachedMilestones } from "@features/programs/services/notificationService";
import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import Tooltip from "react-bootstrap/Tooltip";
import useMediaQuery from "@mui/material/useMediaQuery";
import {
  programWeek,
  programMilestones,
  wellbeing,
  sessionOutcome,
} from "@features/programs/utils/gamification";
import {
  WeekFact,
  ProgramPath,
  WellbeingBlock,
  SidebarExtras,
  SessionComplete,
  FeedbackReward,
} from "./gamification";

function renderExercises(exercises, id) {
  let exercise_content = [];
  for (let i = 0; i < exercises?.length; i++) {
    if (exercises[i].id.toString() === id) {
      for (let j = 0; j < exercises[i].exercises?.length; j++) {
        exercise_content.push(
          <ExerciseCard
            exercise={exercises[i].exercises[j]}
            position={j + 1}
            key={exercises[i].exercises[j].id}
          />
        );
      }
    }
  }
  return exercise_content;
}

/**
 * Some of these translation values already end in a colon ("Ziel:"). The
 * sidebar sets its labels as small caps without punctuation, so drop a
 * trailing colon rather than changing keys other screens rely on.
 */
const sidebarLabel = (value) => String(value ?? "").replace(/\s*:\s*$/, "");

function getProgramSessions(exercises, id) {
  let sessions = [];
  for (let i = 0; i < exercises?.length; i++) {
    if (exercises[i].id.toString() === id) {
      for (let j = 0; j < exercises[i].sessions?.length; j++) {
        sessions.push(exercises[i].sessions[j]);
      }
    }
  }
  return sessions;
}

function setExerciseLayout(data, columns) {
  let content_block = [];
  let content = null;
  for (let i = 0; i < data?.length; i += columns) {
    content = (
      <Row className="program-exercise-row" key={i}>
        <Col md={12} lg={6} xxl={4}>
          {data[i]}
        </Col>
        {(() => {
          if (data[i + 1] !== undefined && columns >= 2) {
            return (
              <Col md={12} lg={6} xxl={4}>
                {data[i + 1]}
              </Col>
            );
          } else {
          }
        })()}
        {(() => {
          if (data[i + 2] !== undefined && columns === 3) {
            return (
              <Col md={12} lg={6} xxl={4}>
                {data[i + 2]}
              </Col>
            );
          }
        })()}
      </Row>
    );
    content_block.push(content);
  }
  return content_block;
}

function FeedbackWindow(props) {
  const [feedbackLvl, setLvl] = useState(5);
  const [feedbackText, setFeedbackText] = useState("");
  const [questionnaireAnswers, setQuestionnaireAnswers] = useState({});
  // The saved session, judged — set instead of the old alert().
  const [completion, setCompletion] = useState(null);
  const { t } = useTranslation();
  // The program with its sessions and the patient's other programs feed
  // the reward block and the completion view; react-bootstrap must not
  // see them.
  const { programData, programs, ...modalProps } = props;

  const hasQuestionnaire = props.questionnaireTemplate?.content?.length > 0;

  // Initialize questionnaire answers with default values when questionnaire is loaded
  useEffect(() => {
    if (hasQuestionnaire && props.questionnaireTemplate?.content) {
      const defaultAnswers = {};
      props.questionnaireTemplate.content.forEach((field, index) => {
        if (field.type === "scale") {
          // Initialize scale fields with their minimum value
          defaultAnswers[index] = field.min || 0;
        }
        // Other field types will initialize as undefined and get handled properly
      });
      setQuestionnaireAnswers(defaultAnswers);
    }
  }, [hasQuestionnaire, props.questionnaireTemplate]);
  
  const handleFeedbackSubmit = async () => {
    try {
      if (hasQuestionnaire) {
        // Submit questionnaire responses (server also creates ProgramSession)
        const url_q = `/:locale/v2/:token/programs/${props.program}/questionnaire_responses.json`;
        const payload = {
          questionnaire_template_id: props.questionnaireTemplate.id,
          responses: questionnaireAnswers,
          pain_level: feedbackLvl,
          program_feedback: feedbackText,
        };
        await http.post(url_q, payload, { headers: { "Content-Type": "application/json" } });
        
        // Create notifications for questionnaire feedback
        if (feedbackText && feedbackText.trim()) {
          await createProgramFeedbackNotification(props.program, props.programName, feedbackText);
        }
        await createProgramCompletedNotification(props.program, props.programName);
      } else {
        // Default feedback only
        await sendFeedback(feedbackLvl, feedbackText, props.program, props.programName);
      }
      
      if (programData) {
        const outcome = sessionOutcome(programData, programs, {
          pain_level: Number(feedbackLvl),
          program_feedback: feedbackText,
        });
        notifyReachedMilestones(props.program, props.programName, outcome.reachedMilestones);
        setCompletion(outcome);
      } else {
        alert(t("feedback.feedback_sent_successfully"));
        props.onHide();
      }
    } catch (error) {
      console.error("Error sending feedback:", error);
      alert(t("feedback.feedback_error"));
    }
  };

  return (
    <Modal
      {...modalProps}
      onHide={completion ? () => window.location.reload() : props.onHide}
      size="lg"
      aria-labelledby="contained-modal-title-vcenter"
      centered
    >
      <Modal.Header closeButton>
        <Modal.Title id="contained-modal-title-vcenter">
          {t("feedback.send-therapist")}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {completion ? (
          <SessionComplete
            outcome={completion}
            programName={props.programName}
            onDone={() => window.location.reload()}
          />
        ) : hasQuestionnaire ? (
          <div>
            <h4 style={{ textAlign: "center", marginBottom: "20px" }}>
              {props.questionnaireTemplate.name}
            </h4>
            <QuestionnaireDecoder
              content={props.questionnaireTemplate.content}
              readOnly={false}
              answers={questionnaireAnswers}
              onChange={(idx, value) => {
                setQuestionnaireAnswers((prev) => {
                  const next = { ...prev };
                  if (typeof value === "object" && value.option) {
                    // multi-select
                    const arr = Array.isArray(next[idx]) ? next[idx] : [];
                    if (value.checked) {
                      next[idx] = [...arr, value.option];
                    } else {
                      next[idx] = arr.filter((v) => v !== value.option);
                    }
                  } else {
                    next[idx] = value;
                  }
                  return next;
                });
              }}
            />
          </div>
        ) : (
          <>
            <FeedbackForm
              feedbackLvl={feedbackLvl}
              setLvl={setLvl}
              feedbackText={feedbackText}
              setFeedbackText={setFeedbackText}
              onSubmit={handleFeedbackSubmit}
            />
            {programData && (
              <div style={{ maxWidth: "460px", margin: "18px auto 0" }}>
                <FeedbackReward
                  program={programData}
                  programs={programs}
                  pendingValue={feedbackLvl}
                  pendingFeedback={feedbackText}
                />
              </div>
            )}
          </>
        )}
      </Modal.Body>
      {hasQuestionnaire && !completion && (
        <Modal.Footer>
          <Button variant="secondary" onClick={props.onHide}>
            {t("actions.cancel")}
          </Button>
          <Button
            style={{ backgroundColor: "var(--main-color)", borderColor: "var(--main-color)" }}
            onClick={handleFeedbackSubmit}
          >
            {t("actions.submit")}
          </Button>
        </Modal.Footer>
      )}
    </Modal>
  );
}

async function sendFeedback(lvl, feedbacktext, id, programName) {
  const url_feedback = "/:locale/v2/:token/program_session.json";
  var params = new FormData();
  params.append("program_session[pain_level]", lvl);
  params.append("program_session[program_id]", id);
  params.append("program_session[program_feedback]", feedbacktext);

  try {
    await http.post(url_feedback, params);
    
    // Create notification for feedback
    if (feedbacktext && feedbacktext.trim()) {
      await createProgramFeedbackNotification(id, programName, feedbacktext);
    }
    
    // Create notification for program completion (if this was the last session)
    // Note: This is a simplified check - you might want to add more sophisticated logic
    await createProgramCompletedNotification(id, programName);
    
  } catch (error) {
    console.error("Error sending feedback:", error);
    throw error;
  }
}

function workoutDoneForToday(last_session) {
  let today = new Date();

  if (
    last_session != null &&
    last_session.setHours(0, 0, 0, 0) === today.setHours(0, 0, 0, 0)
  ) {
    return true;
  }
  return false;
}

function Program() {
  const { id } = useParams();
  const url = `/:locale/v2/:token/programs/${id}`;
  // This program with its exercises - not, as before, every program of the
  // patient - and the light summary of all programs for the badges that
  // span programs (first session, reporter, early bird).
  const url_exercises = `/:locale/v2/:token/programs/get_program_data?id=${id}`;
  const url_summary = "/:locale/v2/:token/programs/summary";

  const [modalShow, setModalShow] = useState(false);
  const [modalProgram, setModalProgramShow] = useState(false);
  const [showSlideshow, setShowSlideshow] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  // Set once a session is saved: the slideshow shows what it changed, and
  // closing it reloads the page with the new session in place.
  const [completion, setCompletion] = useState(null);
  // Below 768px the sidebar stacks above the exercises (see Programs.css).
  const isStacked = useMediaQuery("(max-width:767px)");
  // Exercise cards per row, matching the lg / xxl columns in
  // setExerciseLayout. Read as media queries so that turning a tablet
  // regroups the cards instead of keeping the count from page load.
  const twoColumns = useMediaQuery("(min-width:900px)");
  const threeColumns = useMediaQuery("(min-width:1440px)");
  const exerciseColumns = threeColumns ? 3 : twoColumns ? 2 : 1;

  // Request modal state
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [requestMessage, setRequestMessage] = useState("");
  const [isSubmittingRequest, setIsSubmittingRequest] = useState(false);
  const [canRequest, setCanRequest] = useState(true);
  const [daysUntilNext, setDaysUntilNext] = useState(0);
  const [isCheckingEligibility, setIsCheckingEligibility] = useState(false);
  
  // Subscription contact modal state
  const [showSubscriptionContactModal, setShowSubscriptionContactModal] = useState(false);
  const [subscriptionContactMessage, setSubscriptionContactMessage] = useState("");
  const [isSubmittingSubscriptionContact, setIsSubmittingSubscriptionContact] = useState(false);

  let program = useAxiosGet(url);
  let exercises = useAxiosGet(url_exercises);
  let summary = useAxiosGet(url_summary);
  let sorted_program_sessions;
  let content = null;

  // Check eligibility when component mounts
  React.useEffect(() => {
    checkEligibility();
  }, []);

  const checkEligibility = async () => {
    console.log("Checking eligibility in Program.js");
    setIsCheckingEligibility(true);
    try {
      const response = await canRequestNewProgram();
      setCanRequest(response.can_request);
      setDaysUntilNext(response.days_until_next);
    } catch (error) {
      console.error("Error checking eligibility:", error);
      setCanRequest(true); // Fallback to allowing request
    } finally {
      setIsCheckingEligibility(false);
    }
  };

  const handleClick = async (e) => {
    let last_session = null;
    if (typeof sorted_program_sessions === "undefined") {
      last_session = sorted_program_sessions[0].created_at;
    }

    if (workoutDoneForToday(last_session)) {
      alert("Maximal eine Session pro Tag erlaubt");
    } else {
      setModalShow(true);
    }
  };

  const startSlideshow = async (e) => {
    setCurrentIndex(0);
    setShowSlideshow(true);
  };

  const handleNext = (props) => {
    console.log(props);
    if (currentIndex < props.length) { // Allow going to feedback slide (+1)
      setCurrentIndex((prevIndex) => prevIndex + 1);
    }
  };

  const handlePrevious = () => {
    if (currentIndex > 0) {
      setCurrentIndex((prevIndex) => prevIndex - 1);
    }
  };

  const handleRequestNewProgram = () => {
    setRequestMessage("");
    setShowRequestModal(true);
  };

  const handleSubmitRequest = async () => {
    if (!requestMessage.trim()) {
      alert(t("program.request_message_required"));
      return;
    }

    setIsSubmittingRequest(true);
    try {
      await createProgramRequestNotification(id, requestMessage);
      alert(t("program.request_sent_successfully"));
      setShowRequestModal(false);
      setRequestMessage("");
      // Refresh eligibility after successful request
      checkEligibility();
    } catch (error) {
      console.error("Error sending request:", error);
      alert(t("program.request_error"));
    } finally {
      setIsSubmittingRequest(false);
    }
  };

  const handleContactTherapistAboutSubscription = () => {
    setSubscriptionContactMessage("");
    setShowSubscriptionContactModal(true);
  };

  const handleSubmitSubscriptionContact = async () => {
    if (!subscriptionContactMessage.trim()) {
      alert(t("program.request_message_required"));
      return;
    }

    setIsSubmittingSubscriptionContact(true);
    try {
      // Get programData from exercises data
      const programData = exercises.data?.find(p => p.id.toString() === id);
      const therapistEmail = programData?.account?.email;
      if (!therapistEmail) {
        alert("Therapist email not available");
        return;
      }
      
      await createSubscriptionContactNotification(id, subscriptionContactMessage, therapistEmail);
      alert(t("program.request_sent_successfully"));
      setShowSubscriptionContactModal(false);
      setSubscriptionContactMessage("");
    } catch (error) {
      console.error("Error sending subscription contact:", error);
      alert(t("program.request_error"));
    } finally {
      setIsSubmittingSubscriptionContact(false);
    }
  };

  const handleSlideshowFeedback = async (feedbackLvl, feedbackText, exercises = []) => {
    // Default (non-questionnaire) slideshow submit: creates the session
    // (pain level + feedback) and per-set details in one call.
    try {
      const url = "/:locale/v2/:token/program_session_details.json";
      await http.post(
        url,
        {
          program_session_detail: {
            program_id: id,
            pain_level: feedbackLvl,
            program_feedback: feedbackText,
            exercises,
          },
        },
        { headers: { "Content-Type": "application/json" } }
      );

      if (feedbackText && feedbackText.trim()) {
        await createProgramFeedbackNotification(id, program.data.name, feedbackText);
      }
      await createProgramCompletedNotification(id, program.data.name);

      return finishSession({ pain_level: Number(feedbackLvl), program_feedback: feedbackText });
    } catch (error) {
      console.error("Error sending feedback:", error);
      alert(t("feedback.feedback_error"));
      return "failed";
    }
  };

  // The saved session, judged: what it did to the week, the streak, the
  // path and the badges. Milestones go to the practice; the modal switches
  // to the completion view and stays open until the patient closes it.
  const finishSession = (session) => {
    const current =
      exercises.data?.find((p) => p.id.toString() === id) || {
        ...program.data,
        sessions: getProgramSessions(exercises.data, id),
      };
    const outcome = sessionOutcome(current, summary.data || [current], session);
    notifyReachedMilestones(id, program.data.name, outcome.reachedMilestones);
    setCompletion(outcome);
    return "completed";
  };

  const { t } = useTranslation();

  let feedback_text = t("feedback.program-today");
  let disabled = "";

  if (program.error) {
    content = <div>Programm nicht gefunden</div>;
  }

  if (program.loading) {
    content = <Loader />;
  }

  if (program.data) {
    console.log("program.data", program.data);
    let exercise_data = renderExercises(exercises.data, id);
    let exercise_data_row = setExerciseLayout(exercise_data, exerciseColumns);
    let status_program = isActive(
      program.data.created_at,
      program.data.duration
    );

    let status;
    let variant;
    if (status_program === true) {
      status = "active";
      variant = "success";
    } else {
      status = "inactive";
      variant = "danger";
    }

    // Check subscription status
    const programData = exercises.data?.find(p => p.id.toString() === id);
    const hasActiveSubscription = programData?.has_active_subscription === true;
    const subscriptionStatus = programData?.subscription_status;
    const subscriptionMessage = programData?.subscription_message;
    
    // Debug logging
    console.log('Program subscription debug:', {
      programId: id,
      hasActiveSubscription,
      subscriptionStatus,
      subscriptionMessage,
      programData
    });
    
    // Only show subscription warning for truly problematic states (not FREE)
    const shouldShowSubscriptionWarning = !hasActiveSubscription && subscriptionStatus !== 'free';
    let program_sessions = getProgramSessions(exercises.data, id);
    sorted_program_sessions = program_sessions
      ?.map((obj) => {
        return { ...obj, created_at: new Date(obj.created_at) };
      })
      .sort((a, b) => b.created_at - a.created_at);
    const plannedSessions = program.data.frequency * program.data.duration;

    // The progress elements read the same record the exercises come from;
    // until it has loaded, the program itself with the sessions found so far.
    const detailProgram = programData || { ...program.data, sessions: program_sessions };
    const week = programWeek(detailProgram);
    const milestones = programMilestones(detailProgram);
    const trend = wellbeing(detailProgram);
    const progressFacts = [
      ...(status === "active"
        ? [{ label: t("gamification.week.title"), value: <WeekFact week={week} />, wide: true }]
        : []),
      ...(isStacked
        ? [
            {
              label: t("gamification.path.title"),
              value: <SidebarExtras milestones={milestones} trend={trend} collapsible />,
              wide: true,
            },
          ]
        : [
            { label: t("gamification.path.title"), value: <ProgramPath milestones={milestones} />, wide: true },
            { label: t("gamification.wellbeing.title"), value: <WellbeingBlock trend={trend} />, wide: true },
          ]),
    ];
    if (
      status === "active" &&
      workoutDoneForToday(sorted_program_sessions[0]?.created_at)
    ) {
      disabled = "btn-disabled";
      feedback_text = t("feedback.program-today-done");
    }
    content = (
      <Container fluid className="h-100">
        <Row className="h-100 program-detail-columns">
          {/* From md up the sidebar keeps a fixed width (Programs.css) and the
              exercises take the rest. */}
          <Col md="auto" className="program-properties h-md-100">
            <SharedProgramSidebar
              className="program-sidebar"
              backHref="/dashboard"
              backLabel={t("program.dashboard")}
              title={parse(program.data.name)}
              statusLabel={
                status_program ? t("program.active") : t("program.expired")
              }
              isActive={status_program}
              progress={{
                label: sidebarLabel(t("program.progress")),
                percent: plannedSessions
                  ? (program_sessions.length / plannedSessions) * 100
                  : 0,
                sub: t("program.sessionsDone", {
                  done: program_sessions.length,
                  planned: plannedSessions,
                }),
              }}
              facts={[
                ...progressFacts,
                {
                  label: sidebarLabel(t("program.target")),
                  value: getTargetName(program.data.goal),
                },
                {
                  label: sidebarLabel(t("program.frequency")),
                  value: programInterval(program.data.frequency),
                },
                {
                  label: sidebarLabel(t("program.duration")),
                  value: `${program.data.duration} ${t("program.weeks")}`,
                },
                {
                  label: sidebarLabel(t("program.end")),
                  value: new Date(
                    getEndDateOfProgram(
                      program.data.created_at,
                      program.data.duration
                    )
                  ).toLocaleDateString("de-DE"),
                },
                {
                  label: sidebarLabel(t("program.note")),
                  value: program.data.description || t("program.no-notes"),
                  wide: true,
                  muted: !program.data.description,
                },
              ]}
              actions={
                <>
                  {status === "active" && (
                    <>
                      <button
                        type="button"
                        className={`sophy-ps-btn sophy-ps-btn-primary ${disabled}`}
                        onClick={() => handleClick()}
                      >
                        {feedback_text}
                      </button>
                      <button
                        type="button"
                        className="sophy-ps-btn"
                        onClick={() => startSlideshow()}
                      >
                        {t("feedback.startSlideShow")}
                      </button>
                    </>
                  )}

                  {status === "inactive" &&
                    (canRequest ? (
                      <button
                        type="button"
                        className="sophy-ps-btn sophy-ps-btn-primary"
                        onClick={handleRequestNewProgram}
                        disabled={isCheckingEligibility}
                      >
                        {isCheckingEligibility
                          ? t("actions.loading")
                          : t("program.request_new_program")}
                      </button>
                    ) : (
                      <OverlayTrigger
                        placement="top"
                        overlay={
                          <Tooltip id="request-tooltip">
                            {daysUntilNext === 1
                              ? t("program.request_cooldown_single")
                              : t("program.request_cooldown", {
                                  days: daysUntilNext,
                                })}
                          </Tooltip>
                        }
                      >
                        <span className="d-inline-block w-100">
                          <button
                            type="button"
                            className="sophy-ps-btn sophy-ps-btn-primary"
                            disabled
                            style={{ pointerEvents: "none" }}
                          >
                            {t("program.request_new_program")}
                          </button>
                        </span>
                      </OverlayTrigger>
                    ))}
                </>
              }
            />
          </Col>
          <Col md className="exercise-details-wrapper">
            {/* Subscription Warning Banner */}
            {shouldShowSubscriptionWarning && subscriptionMessage && (
              <Row className="mb-4">
                <Col md={12}>
                  <div className="alert alert-warning" role="alert">
                    <div className="d-flex justify-content-between align-items-start">
                      <div>
                        <strong>{t("program.subscription_limited")}</strong>
                        <br />
                        <small>{subscriptionMessage}</small>
                      </div>
                      <Button 
                        variant="outline-warning" 
                        size="sm" 
                        onClick={handleContactTherapistAboutSubscription}
                        className="ml-3"
                        style={{ backgroundColor: "var(--main-color)", borderColor: "var(--main-color)" }}
                      >
                        {t("subscription.contact_therapist_about_subscription")}
                      </Button>
                    </div>
                  </div>
                </Col>
              </Row>
            )}
            
            {/* Show exercises only if subscription is active */}
            {exercises.loading ? (
              <Row className="mb-4">
                <Col md={12}>
                  {/* The overlay loader would grey out the whole viewport for
                      a section that is still loading; the same spinner sits
                      inline here. */}
                  <div className="exercises-loading">
                    <span className="loader-spinner" role="status">
                      <span className="visually-hidden">
                        {t("program.loading_exercises")}
                      </span>
                    </span>
                    <p>{t("program.loading_exercises")}</p>
                  </div>
                </Col>
              </Row>
            ) : hasActiveSubscription ? (
              <>
                {exercise_data_row}
                {/* Repeated below the list: on a phone the sidebar carrying the
                    same action sits far above the exercises. */}
                {status === "active" && (
                  <Row className="program-finished d-block d-md-none">
                    <Col>
                      <button
                        type="button"
                        className={`sophy-ps-btn sophy-ps-btn-primary ${disabled}`}
                        onClick={() => handleClick()}
                      >
                        {feedback_text}
                      </button>
                    </Col>
                  </Row>
                )}
              </>
            ) : (
              <Row className="mb-4">
                <Col md={12}>
                  <div className="text-center py-5">
                    <div className="mb-3">
                      <i className="fas fa-lock fa-3x text-muted"></i>
                    </div>
                    <h5 className="text-muted">{t("program.exercises_not_available")}</h5>
                    <p className="text-muted mb-4">{t("program.exercises_subscription_required")}</p>
                    <Button 
                      style={{ backgroundColor: "var(--main-color)", borderColor: "var(--main-color)" }}
                      onClick={handleContactTherapistAboutSubscription}
                    >
                      <i className="fas fa-envelope me-2"></i>
                      {t("subscription.contact_therapist_about_subscription")}
                    </Button>
                  </div>
                </Col>
              </Row>
            )}
          </Col>
          <Col className="last-of-type">-</Col>
        </Row>
        <FeedbackWindow
          show={modalShow}
          onHide={() => setModalShow(false)}
          key={program.data.id}
          program={id}
          programName={program.data.name}
          programData={detailProgram}
          programs={summary.data || []}
          questionnaireTemplate={program?.data?.questionnaire_template}
        ></FeedbackWindow>
        <ProgramModal
          show={modalProgram}
          onHide={() => setModalProgramShow(false)}
          key={program.data.name}
          program={id}
          exercises={exercise_data}
        ></ProgramModal>
        <SlideshowModal
          show={showSlideshow}
          video={exercise_data[currentIndex]}
          onHide={() => (completion ? window.location.reload() : setShowSlideshow(false))}
          program={detailProgram}
          programs={summary.data || []}
          programName={program.data.name}
          completion={completion}
          onSessionSaved={finishSession}
          onNext={() => handleNext(exercise_data)}
          onPrevious={() => handlePrevious(exercise_data)}
          exercises={exercise_data[currentIndex]}
          currentIndex={currentIndex}
          programLength={exercise_data.length}
          onSubmitFeedback={handleSlideshowFeedback}
          questionnaireTemplate={program?.data?.questionnaire_template}
          programId={id}
        />
        
        {/* Request New Program Modal */}
        <Modal show={showRequestModal} onHide={() => setShowRequestModal(false)} centered>
          <Modal.Header closeButton>
            <Modal.Title>{t("program.request_new_program")}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form>
              <Form.Group className="mb-3">
                <Form.Label>{t("program.request_message_label")}</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={4}
                  placeholder={t("program.request_message_placeholder")}
                  value={requestMessage}
                  onChange={(e) => setRequestMessage(e.target.value)}
                />
              </Form.Group>
            </Form>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowRequestModal(false)}>
              {t("actions.cancel")}
            </Button>
            <Button 
              style={{ backgroundColor: "var(--main-color)", borderColor: "var(--main-color)" }}
              onClick={handleSubmitRequest}
              disabled={isSubmittingRequest}
            >
              {isSubmittingRequest ? t("actions.sending") : t("actions.send_request")}
            </Button>
          </Modal.Footer>
        </Modal>
        
        {/* Subscription Contact Modal */}
        <Modal show={showSubscriptionContactModal} onHide={() => setShowSubscriptionContactModal(false)} centered>
          <Modal.Header closeButton>
            <Modal.Title>{t("subscription.contact_therapist_about_subscription")}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form>
              <Form.Group className="mb-3">
                <Form.Label>{t("subscription.contact_message_placeholder")}</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={4}
                  placeholder={t("subscription.contact_message_placeholder2")}
                  value={subscriptionContactMessage}
                  onChange={(e) => setSubscriptionContactMessage(e.target.value)}
                />
              </Form.Group>
            </Form>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowSubscriptionContactModal(false)}>
              {t("actions.cancel")}
            </Button>
            <Button 
              style={{ backgroundColor: "var(--main-color)", borderColor: "var(--main-color)" }}
              onClick={handleSubmitSubscriptionContact}
              disabled={isSubmittingSubscriptionContact}
            >
              {isSubmittingSubscriptionContact ? t("actions.sending") : t("subscription.send_contact_message")}
            </Button>
          </Modal.Footer>
        </Modal>
      </Container>
    );
  }

  return <div className="h-100">{content}</div>;
}

export default Program;
